import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useFetch } from "../../hooks/useFetch";
import { Loading } from "../../components/States";
import { CCard, Pill } from "../../components/customer/ui";
import { FileWorkspace } from '../../components/FileWorkspace';
import { Messages } from '../../components/Messages';
import {
  ArrowLeft, MapPin, Phone, CheckCircle2, Circle, Sparkles, CalendarClock,
  Users, Camera, FileText, MessageSquare, Download,
} from "lucide-react";

const TABS = ["Overview", "Timeline", "Photos", "Documents", "Messages"];

function buildProgress(job) {
  if (Array.isArray(job.progress) && job.progress.length) return job.progress;
  const base = ['Scheduled', 'Work in Progress', 'Project Complete'];
  const doneTo = job.status === 'complete' ? 3 : job.status === 'in_progress' ? 2 : job.status === 'scheduled' ? 1 : 0;
  return base.map((label, i) => ({ label, done: i < doneTo, date: null }));
}
function pct(job, prog) {
  if (typeof job.percent === "number") return job.percent;
  return Math.round((prog.filter((s) => s.done).length / prog.length) * 100);
}
const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");

export default function ProjectTracking() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: job, loading } = useFetch(`/jobs/${id}`);
  const [tab, setTab] = useState("Overview");

  if (loading) return <Loading />;
  if (!job) return <div className="py-20 text-center text-slate-400" data-testid="project-not-found">Project not found.</div>;

  const prog = buildProgress(job);
  const percent = pct(job, prog);
  const img = job.image;
  const photos = job.photos || [];
  const docs = job.documents || [];

  return (
    <div className="space-y-6" data-testid="project-tracking">
      <button onClick={() => navigate("/portal/projects")} className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800" data-testid="back-to-projects">
        <ArrowLeft size={16} /> Back to Projects
      </button>

      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">{job.title}</h1>
            <Pill status={job.status} />
          </div>
          <div className="mt-1.5 flex items-center gap-2 text-slate-500"><MapPin size={16} /> {job.address}</div>
        </div>
        <a href="tel:3526153878" className="flex items-center gap-2 rounded-xl bg-[#1286F2] px-5 py-2.5 font-bold text-white hover:bg-[#0A84FF]" data-testid="contact-us">
          <Phone size={16} /> Contact Us
        </a>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto border-b border-slate-200">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-semibold transition ${tab === t ? "border-[#1286F2] text-[#1286F2]" : "border-transparent text-slate-500 hover:text-slate-800"}`} data-testid={`tab-${t.toLowerCase()}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === "Overview" && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Progress */}
          <CCard className="p-6 lg:col-span-2">
            <h2 className="text-xl font-bold text-slate-900">Project Progress</h2>
            <div className="mt-4 flex items-center gap-3">
              <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-200"><div className="h-full rounded-full bg-[#1286F2] transition-all" style={{ width: `${percent}%` }} /></div>
              <span className="text-sm font-bold text-slate-700">{percent}%</span>
            </div>
            <ul className="mt-6 space-y-4" data-testid="progress-steps">
              {prog.map((s, i) => (
                <li key={i} className="flex items-center gap-3">
                  {s.done ? <CheckCircle2 size={22} className="shrink-0 text-[#1286F2]" /> : <Circle size={22} className="shrink-0 text-slate-300" />}
                  <span className={`flex-1 ${s.done ? "font-semibold text-slate-800" : "text-slate-400"}`}>{s.label}</span>
                  <span className="text-sm text-slate-400">{fmt(s.date)}</span>
                </li>
              ))}
            </ul>
          </CCard>

          {/* Details + image + actions */}
          <div className="space-y-6">
            <CCard className="p-6">
              <h2 className="text-xl font-bold text-slate-900">Project Details</h2>
              <dl className="mt-4 space-y-3 text-sm">
                <Detail icon={Sparkles} label="Service Type" value={job.service_category || job.title} />
                <Detail icon={CalendarClock} label="Start Date" value={fmt(job.started_at || job.scheduled_date)} />
                <Detail icon={CalendarClock} label="Est. Completion" value={fmt(job.est_completion)} />
                <Detail icon={Users} label="Assigned Crew" value={job.crew || job.assigned_name} />
              </dl>
            </CCard>
            {img && <CCard className="overflow-hidden">
              <img src={img} alt={`${job.title} — previously supplied image`} className="h-44 w-full object-cover" />
            </CCard>}
            <div className="grid grid-cols-3 gap-3">
              <ActionCard icon={Camera} label="View Photos" onClick={() => setTab("Photos")} testid="action-photos" />
              <ActionCard icon={FileText} label="Documents" onClick={() => setTab("Documents")} testid="action-documents" />
              <ActionCard icon={MessageSquare} label="Message Crew" onClick={() => setTab("Messages")} testid="action-messages" />
            </div>
          </div>
        </div>
      )}

      {tab === "Timeline" && (
        <CCard className="p-6" >
          <ol className="relative ml-3 border-l-2 border-slate-200" data-testid="timeline">
            {prog.map((s, i) => (
              <li key={i} className="mb-6 ml-6">
                <span className={`absolute -left-[11px] flex h-5 w-5 items-center justify-center rounded-full ${s.done ? "bg-[#1286F2]" : "bg-slate-300"}`}>
                  {s.done && <CheckCircle2 size={14} className="text-white" />}
                </span>
                <div className={`font-semibold ${s.done ? "text-slate-800" : "text-slate-400"}`}>{s.label}</div>
                <div className="text-sm text-slate-400">{fmt(s.date)}</div>
              </li>
            ))}
          </ol>
        </CCard>
      )}

      {(tab === 'Photos' || tab === 'Documents') && <FileWorkspace jobId={id} />}
      {tab === 'Messages' && <Messages />}
      {tab === "Photos" && (
        <CCard className="p-6">
          {photos.length === 0 ? (
            <div className="py-10 text-center text-slate-400">No photos yet. Your crew will add job photos as work progresses.</div>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3" data-testid="photo-grid">
              {photos.map((p, i) => (
                <div key={i} className="aspect-square overflow-hidden rounded-xl border border-slate-200">
                  <img src={p} alt={`Job photo ${i + 1}`} className="h-full w-full object-cover" />
                </div>
              ))}
            </div>
          )}
        </CCard>
      )}

      {tab === "Documents" && (
        <CCard className="divide-y divide-slate-100" >
          {docs.length === 0 ? (
            <div className="py-10 text-center text-slate-400">No documents available yet.</div>
          ) : docs.map((d, i) => (
            <div key={i} className="flex items-center gap-4 px-6 py-4" data-testid={`doc-${i}`}>
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-[#1286F2]"><FileText size={18} /></div>
              <div className="min-w-0 flex-1">
                <div className="truncate font-semibold text-slate-800">{d.name}</div>
                <div className="text-sm text-slate-400">{d.type} · {fmt(d.date)}</div>
              </div>
              <a href="tel:3526153878" className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-600 hover:border-[#1286F2] hover:text-[#1286F2]"><Download size={14} /> Request</a>
            </div>
          ))}
        </CCard>
      )}


    </div>
  );
}

function Detail({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon size={17} className="mt-0.5 shrink-0 text-slate-400" />
      <div>
        <dt className="font-semibold text-slate-700">{label}</dt>
        <dd className="text-slate-500">{value}</dd>
      </div>
    </div>
  );
}

function ActionCard({ icon: Icon, label, onClick, testid }) {
  return (
    <button onClick={onClick} className="flex flex-col items-center gap-2 rounded-2xl border border-slate-200 bg-white p-4 text-center shadow-sm transition hover:-translate-y-0.5 hover:border-[#1286F2]/40 hover:shadow-md" data-testid={testid}>
      <Icon size={22} className="text-[#1286F2]" />
      <span className="text-xs font-semibold text-slate-700">{label}</span>
    </button>
  );
}
